import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import { dispatchMetaTransaction } from '@/lib/zy-tiptap-utils';
import { type ReactNodeViewProps, NodeViewContent } from '@tiptap/react';
import { XIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

const MatchingPairNodeView: React.FC<ReactNodeViewProps> = ({ node, editor, getPos, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id } = node.attrs;

    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'matchingPair',
    });

    const handleMoveUp = useCallback(() => {
        if (nodeIndex <= 0) return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'up', { pos, node });
        dispatchMetaTransaction(editor, { matchingPairUpdate: true });
    }, [editor, getPos, node, nodeIndex]);

    const handleMoveDown = useCallback(() => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'down', { pos, node });
        dispatchMetaTransaction(editor, { matchingPairUpdate: true });
    }, [editor, getPos, node]);

    const handleDelete = useCallback(() => {
        // Check if we have at least 3 pairs (minimum 2 required)
        if (totalNodes <= 2) {
            return;
        }
        if (window.confirm('Are you sure you want to delete this pair?')) {
            deleteNode();
            dispatchMetaTransaction(editor, { matchingPairUpdate: true });
        }
    }, [deleteNode, totalNodes, editor]);

    const isDeleteDisabled = () => {
        return totalNodes <= 2;
    };

    const isMoveUpDisabled = () => {
        return nodeIndex == 0 || totalNodes <= 1;
    };

    const isMoveDownDisabled = () => {
        return nodeIndex === totalNodes - 1 || totalNodes <= 1;
    };

    return (
        <ZyNodeViewWrapper
            decorations={decorations}
            key={id}
            data-testid="matching-pair-node-view"
        >
            <div className="relative my-2 flex itmes-start gap-3 rounded-lg border-2 border-gray-200 bg-white pt-2 pl-1 pb-3 pr-8 dark:border-gray-700 dark:bg-gray-900 transition-all ">
                {/* Move up and down buttons */}
                <div
                    className="mt-0.5 flex shrink-0 items-start gap-2"
                    contentEditable={false}
                >
                    <div className="flex items-center gap-0.5 flex-col">
                        <Button
                            onClick={handleMoveUp}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-950/20 dark:hover:text-gray-400"
                            data-style="ghost"
                            aria-label="Move pair up"
                            disabled={isMoveUpDisabled() || isEditorReadonly}
                            tooltip={isMoveUpDisabled() ? 'This is the first pair in the Matching set' : 'Move pair up'}
                        >
                            <AlignTopIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-950/20 dark:hover:text-gray-400"
                            data-style="ghost"
                            aria-label="Move pair down"
                            disabled={isMoveDownDisabled() || isEditorReadonly}
                            tooltip={isMoveDownDisabled() ? 'This is the last pair in the Matching set' : 'Move pair down'}
                        >
                            <AlignBottomIcon className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>

                {/* Term, Definition, and Explanation Nodes */}
                <NodeViewContent className="w-full" />

                <div
                    className="flex absolute top-2 right-0"
                    contentEditable={false}
                >
                    {/* Delete button */}
                    <Button
                        onClick={handleDelete}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-all hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-950/20 dark:hover:text-red-500"
                        contentEditable={false}
                        data-style="ghost"
                        aria-label="Delete pair"
                        title="Delete pair"
                        disabled={isDeleteDisabled() || isEditorReadonly}
                        tooltip={isDeleteDisabled() ? 'A Matching set must have at least two pairs' : 'Delete pair'}
                    >
                        <XIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </ZyNodeViewWrapper>
    );
};

export default memo(MatchingPairNodeView);
